import { ISOToTimestamp } from '@/utils/grpc-util'
import { Interval } from '@taxfyle/api-internal/shared/types/interval_pb'
import { addMinutes, format, isBefore, differenceInDays } from 'date-fns'

/**
 * Represents a range of time with a start and end time.
 */
export class TimeRange {
  constructor(
    public readonly startTime: Date,
    public readonly endTime: Date,
  ) {}

  static of(startTime: Date, endTime: Date): TimeRange {
    return new TimeRange(startTime, endTime)
  }

  /**
   * Gets the number of days until the start time
   * @returns
   */
  get daysUntilStart(): number {
    return differenceInDays(this.startTime, new Date())
  }

  /**
   * Checks if this time range is equal to another time range
   * by comparing hours and minutes. Does not check for the actual date.
   */
  isTimeEqual(range: TimeRange): boolean {
    return (
      range.startTime.getHours() === this.startTime.getHours() &&
      range.startTime.getMinutes() === this.startTime.getMinutes() &&
      range.endTime.getHours() === this.endTime.getHours() &&
      range.endTime.getMinutes() === this.endTime.getMinutes()
    )
  }

  /**
   * Checks if the time range is equal to another time range
   * by comparing the exact start and end times.
   */
  isEqual(range: TimeRange): boolean {
    return (
      this.startTime.getTime() === range.startTime.getTime() &&
      this.endTime.getTime() === range.endTime.getTime()
    )
  }

  /**
   * Checks if the time range is on the same day as the given date
   */
  isSameDay(date: Date): boolean {
    return this.startTime.toDateString() === date.toDateString()
  }

  /**
   * Decomposes the time range into smaller intervals
   * @param intervalInMinutes
   * @returns
   */
  decomposeRangeInIntervals(intervalInMinutes: number): TimeRange[] {
    if (intervalInMinutes <= 0) {
      return []
    }

    let cursor = new Date(this.startTime)
    const intervals: TimeRange[] = []

    while (isBefore(cursor, this.endTime)) {
      const end = addMinutes(cursor, intervalInMinutes)
      intervals.push(new TimeRange(new Date(cursor), end))
      cursor = end
    }

    return intervals
  }

  /**
   * Returns the starting time of the range in a human-readable format
   * @returns
   */
  toStartingHourString(): string {
    return format(this.startTime, 'hh:mm a')
  }

  /**
   * Returns the starting day of the range in a human-readable format
   * @returns
   */
  toStartingDayString(): string {
    return format(this.startTime, 'MMM d, yyyy')
  }

  /**
   * Converts the time range to an interval
   * @returns
   */
  toProtoInterval(): Interval {
    return new Interval()
      .setStartTime(ISOToTimestamp(this.startTime.toISOString()))
      .setEndTime(ISOToTimestamp(this.endTime.toISOString()))
  }
}
